// hooks/usePropertyViews.ts
import { useEffect, useCallback } from 'react';
import {
  collection,
  query,
  where,
  getDocs,
  updateDoc,
  increment
} from 'firebase/firestore';
import { db } from '../auth/firebaseConfig';

export const usePropertyViews = (
  propertyId: string | undefined,
  onViewIncremented?: () => void
) => {
  const updatePropertyViews = useCallback(
    async (id: string) => {
      // Get viewed properties from sessionStorage
      const viewedProperties = sessionStorage.getItem('viewedProperties');
      const viewedSet = viewedProperties
        ? new Set(JSON.parse(viewedProperties))
        : new Set();

      // If property has already been viewed in this session, don't increment
      if (viewedSet.has(id)) {
        return;
      }

      try {
        // Query the listing by the custom 'id' field (not Firestore doc ID)
        const listingsRef = collection(db, 'listings');
        const q = query(listingsRef, where('id', '==', Number(id)));
        const querySnapshot = await getDocs(q);

        if (querySnapshot.empty) {
          return;
        }

        // Get the first (and should be only) document
        const listingDoc = querySnapshot.docs[0];

        // Update view count using the Firestore document reference
        await updateDoc(listingDoc.ref, { propertyViews: increment(1) });

        // Mark property as viewed in this session
        viewedSet.add(id);
        sessionStorage.setItem(
          'viewedProperties',
          JSON.stringify([...viewedSet])
        );

        // Notify parent component that view was incremented
        if (onViewIncremented) {
          onViewIncremented();
        }
      } catch (error) {
        console.error('Error updating property view count:', error);
      }
    },
    [onViewIncremented]
  );

  useEffect(() => {
    if (propertyId) {
      updatePropertyViews(propertyId);
    }
  }, [propertyId, updatePropertyViews]);
};
